import { Calendar, ChevronDown, ChevronLeft, ChevronRight, RefreshCw, Search } from 'lucide-react';
import { useMemo, useState } from 'react';
import { ErrorState, LoadingState } from '@/components/ui/states';
import { useViewMessages } from '@/lib/i18n';
import {
  type BookingRow,
  type BookingStatus,
  type ReconciliationData,
  useArchiveBooking,
  useBookingQueue,
  useDeleteBooking,
  useReconciliation,
  useSetSyncStatus,
} from './data';

const MESSAGES = {
  nb: {
    title: 'Bookinger',
    sub: 'Prøvetimer booket via landingssiden. Synkes til Momence av Claude Cowork.',
    coworkActive: 'Cowork-sync aktiv',
    coworkIdle: 'Manuell synk — Cowork ikke aktiv',
    refresh: 'Oppdater',
    statNew: 'Nye – ikke synket',
    statSynced: 'Synket til Momence',
    statErr: 'Trenger oppfølging',
    statTotal: 'Totalt',
    all: 'Alle',
    new: 'Nye',
    synced: 'Synket',
    error: 'Feil',
    search: 'Søk navn, ref, telefon…',
    allDates: 'Alle datoer',
    shortcuts: 'Snarveier',
    today: 'I dag',
    tomorrow: 'I morgen',
    next7: 'Neste 7 dager',
    next30: 'Neste 30 dager',
    cancel: 'Avbryt',
    apply: 'Bruk',
    pickDates: 'Velg datoer',
    colStatus: 'Status',
    colName: 'Navn',
    colContact: 'Kontakt',
    colSlot: 'Prøvetime',
    colNote: 'Merknad',
    colBooked: 'Booket',
    colAction: 'Handling',
    stNew: 'Ny',
    stSynced: 'Synket',
    stError: 'Feil',
    stCancelled: 'Avbrutt',
    markSynced: 'Marker synket',
    inMomence: 'I Momence',
    undo: 'Angre',
    retry: 'Prøv igjen',
    archive: 'Arkiver',
    restore: 'Gjenopprett',
    del: 'Slett',
    confirmDelete: 'Slette denne bookingen permanent? Dette kan ikke angres.',
    archived: 'Arkivert',
    custOk: 'Momence-kunde',
    custFail: 'Kunde ikke opprettet',
    intakeTitle: 'Svar fra kunden',
    viewAnswers: 'Se svar',
    hideAnswers: 'Skjul svar',
    srcTitle: 'Kilde / annonse',
    srcAd: 'Annonse',
    srcCampaign: 'Kampanje',
    srcAdset: 'Annonsesett',
    srcAdId: 'Annonse-ID',
    srcMeta: 'Meta-sporing',
    srcFacebook: 'Facebook-annonse',
    srcDirect: 'Direkte / ukjent kilde',
    srcViewDetails: 'Se kilde',
    capiSent: 'Sendt til Meta ✓',
    pageQuiz: 'Quiz',
    pageQuizPage: 'Quiz-side',
    pageQuizPageHint: 'Bookdet fra quiz-siden uten å fullføre quizen',
    pageLp: 'Landingsside',
    atTime: (t: string) => `kl. ${t}`,
    reconTitle: 'Annonse-attribusjon',
    reconSub: 'Bekreftet fra annonse vs. Meta-tilskrevne Schedule-konverteringer',
    reconTotal: 'Bookinger (perioden)',
    reconConfirmed: '📣 Bekreftet fra annonse',
    reconDirect: 'Direkte / uten klikk-ID',
    reconMeta: 'Meta tilskriver annonser',
    reconGap: 'Gap — annonsepåvirket, men usporbar:',
    reconNote:
      '«Bekreftet» = booking med klikk-ID eller UTM (sikker bunn). Meta krediterer per annonse i aggregat (7-dagers klikk), aldri per person — gapet er bookinger Meta tilskriver annonser som vi ikke kunne knytte til et klikk på besøket. Stol på Meta Ads Manager for total betalt-kreditt.',
    close: 'Lukk',
    prevMonth: 'Forrige måned',
    nextMonth: 'Neste måned',
    intakeLabels: {
      alder: 'Alder',
      plager: 'Plager / hensyn',
      erfaring: 'Trent styrke før',
      grunner: 'Viktigste grunner',
    } as Record<string, string>,
    empty: 'Ingen bookinger matcher.',
    emptyAll: 'Ingen bookinger ennå. De dukker opp her i det en kunde booker.',
    loadFailed: 'Kunne ikke laste bookinger.',
    wd: ['Man', 'Tir', 'Ons', 'Tor', 'Fre', 'Lør', 'Søn'],
    monL: [
      'januar',
      'februar',
      'mars',
      'april',
      'mai',
      'juni',
      'juli',
      'august',
      'september',
      'oktober',
      'november',
      'desember',
    ],
    monShort: ['jan', 'feb', 'mar', 'apr', 'mai', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'des'],
    /** BCP47 tag for the Intl weekday/month names rendered in this view. */
    intlLocale: 'nb-NO',
    dayLong: (wd: string, day: string, mon: string) => `${wd} ${day}. ${mon}`,
    dateShort: (day: number, mon: string) => `${day}. ${mon}`,
    relToday: (t: string) => `I dag ${t}`,
    relYesterday: (t: string) => `I går ${t}`,
    relDate: (day: number, mon: string, t: string) => `${day}. ${mon} ${t}`,
  },
  en: {
    title: 'Bookings',
    sub: 'Trials booked via the landing page. Synced to Momence by Claude Cowork.',
    coworkActive: 'Cowork sync active',
    coworkIdle: 'Manual sync — Cowork not active',
    refresh: 'Refresh',
    statNew: 'New – not synced',
    statSynced: 'Synced to Momence',
    statErr: 'Needs follow-up',
    statTotal: 'Total',
    all: 'All',
    new: 'New',
    synced: 'Synced',
    error: 'Error',
    search: 'Search name, ref, phone…',
    allDates: 'All dates',
    shortcuts: 'Shortcuts',
    today: 'Today',
    tomorrow: 'Tomorrow',
    next7: 'Next 7 days',
    next30: 'Next 30 days',
    cancel: 'Cancel',
    apply: 'Apply',
    pickDates: 'Pick dates',
    colStatus: 'Status',
    colName: 'Name',
    colContact: 'Contact',
    colSlot: 'Trial',
    colNote: 'Note',
    colBooked: 'Booked',
    colAction: 'Action',
    stNew: 'New',
    stSynced: 'Synced',
    stError: 'Error',
    stCancelled: 'Cancelled',
    markSynced: 'Mark synced',
    inMomence: 'In Momence',
    undo: 'Undo',
    retry: 'Retry',
    archive: 'Archive',
    restore: 'Restore',
    del: 'Delete',
    confirmDelete: 'Permanently delete this booking? This cannot be undone.',
    archived: 'Archived',
    custOk: 'Momence customer',
    custFail: 'Customer not created',
    intakeTitle: 'Customer answers',
    viewAnswers: 'View answers',
    hideAnswers: 'Hide answers',
    srcTitle: 'Source / ad',
    srcAd: 'Ad',
    srcCampaign: 'Campaign',
    srcAdset: 'Ad set',
    srcAdId: 'Ad ID',
    srcMeta: 'Meta tracking',
    srcFacebook: 'Facebook ad',
    srcDirect: 'Direct / unknown source',
    srcViewDetails: 'View source',
    capiSent: 'Sent to Meta ✓',
    pageQuiz: 'Quiz',
    pageQuizPage: 'Quiz page',
    pageQuizPageHint: 'Booked from the quiz page without completing the quiz',
    pageLp: 'Landing page',
    atTime: (t: string) => `at ${t}`,
    reconTitle: 'Ad attribution',
    reconSub: 'Confirmed from an ad vs. Meta-attributed Schedule conversions',
    reconTotal: 'Bookings (period)',
    reconConfirmed: '📣 Confirmed from an ad',
    reconDirect: 'Direct / no click ID',
    reconMeta: 'Meta attributes to ads',
    reconGap: 'Gap — ad-influenced, but untrackable:',
    reconNote:
      '“Confirmed” = a booking carrying a click ID or UTM (the safe floor). Meta credits per ad in aggregate (7-day click), never per person — the gap is bookings Meta attributes to ads that we could not tie to a click on the visit. Trust Meta Ads Manager for total paid credit.',
    close: 'Close',
    prevMonth: 'Previous month',
    nextMonth: 'Next month',
    intakeLabels: {
      alder: 'Age',
      plager: 'Conditions / notes',
      erfaring: 'Strength-training experience',
      grunner: 'Main reasons',
    } as Record<string, string>,
    empty: 'No bookings match.',
    emptyAll: 'No bookings yet. They appear here the moment a customer books.',
    loadFailed: 'Could not load bookings.',
    wd: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
    monL: [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December',
    ],
    monShort: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
    /** BCP47 tag for the Intl weekday/month names rendered in this view. */
    intlLocale: 'en-GB',
    dayLong: (wd: string, day: string, mon: string) => `${wd} ${day} ${mon}`,
    dateShort: (day: number, mon: string) => `${mon} ${day}`,
    relToday: (t: string) => `Today ${t}`,
    relYesterday: (t: string) => `Yesterday ${t}`,
    relDate: (day: number, mon: string, t: string) => `${mon} ${day}, ${t}`,
  },
};
type ViewMessages = (typeof MESSAGES)['nb'];

const pad = (n: number) => String(n).padStart(2, '0');

// Oslo calendar key + display parts for an ISO instant.
function osloParts(iso: string) {
  const fmt = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Oslo',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  const p = fmt.formatToParts(new Date(iso));
  const g = (t: string) => p.find((x) => x.type === t)?.value ?? '';
  return { key: `${g('year')}-${g('month')}-${g('day')}` };
}
function osloTime(iso: string) {
  return new Intl.DateTimeFormat('nb-NO', {
    timeZone: 'Europe/Oslo',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(new Date(iso));
}
function osloDay(iso: string, m: ViewMessages) {
  const d = new Date(iso);
  const wd = new Intl.DateTimeFormat(m.intlLocale, { timeZone: 'Europe/Oslo', weekday: 'short' })
    .format(d)
    .replace('.', '');
  const day = new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Oslo', day: 'numeric' }).format(
    d,
  );
  const mon = new Intl.DateTimeFormat(m.intlLocale, {
    timeZone: 'Europe/Oslo',
    month: 'long',
  }).format(d);
  return m.dayLong(`${wd.charAt(0).toUpperCase()}${wd.slice(1)}`, day, mon);
}
function relCreated(iso: string, m: ViewMessages) {
  const d = new Date(iso);
  const today = new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Oslo' }).format(new Date());
  const that = new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Oslo' }).format(d);
  const t = osloTime(iso);
  if (that === today) return m.relToday(t);
  const y = new Date(Date.now() - 86400000);
  if (that === new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Oslo' }).format(y))
    return m.relYesterday(t);
  const p = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Oslo',
    day: '2-digit',
    month: '2-digit',
  }).formatToParts(d);
  const gg = (k: string) => p.find((x) => x.type === k)?.value ?? '';
  return m.relDate(Number(gg('day')), m.monShort[Number(gg('month')) - 1] ?? '', t);
}

// date math for the picker (YYYY-MM-DD, local-noon anchored)
function mk(y: number, mZero: number, d: number) {
  return `${y}-${pad(mZero + 1)}-${pad(d)}`;
}
function todayKey() {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Oslo' }).format(new Date());
}
function addDaysKey(key: string, n: number) {
  const [y, m, d] = key.split('-').map(Number);
  const dt = new Date(y ?? 1970, (m ?? 1) - 1, (d ?? 1) + n);
  return mk(dt.getFullYear(), dt.getMonth(), dt.getDate());
}
function fmtShort(key: string, msg: ViewMessages) {
  const [, m, d] = key.split('-').map(Number);
  return msg.dateShort(d ?? 1, msg.monShort[(m ?? 1) - 1] ?? '');
}

const STATUS_TONE: Record<BookingStatus, string> = {
  new: 'bg-warn-tint text-warn-fg',
  synced: 'bg-good-tint text-good-fg',
  error: 'bg-bad-tint text-bad-fg',
  cancelled: 'bg-surface-3 text-fg-muted',
};
const STATUS_DOT: Record<BookingStatus, string> = {
  new: 'bg-warn-fg',
  synced: 'bg-good-fg',
  error: 'bg-bad-fg',
  cancelled: 'bg-fg-subtle',
};

const INTAKE_ORDER = ['alder', 'plager', 'erfaring', 'grunner'];
function humanizeKey(k: string) {
  return k.charAt(0).toUpperCase() + k.slice(1).replace(/[_-]/g, ' ');
}
/** Every non-empty intake answer, ordered (known fields first, then any extra),
 *  labelled, with values normalised to string arrays so multi-selects (grunner)
 *  render as chips. This is the full set Cowork will read to fill Momence. */
function intakeEntries(intake: Record<string, unknown>, labels: Record<string, string>) {
  const keys = [
    ...INTAKE_ORDER.filter((k) => k in intake),
    ...Object.keys(intake).filter((k) => !INTAKE_ORDER.includes(k)),
  ];
  const out: Array<{ key: string; label: string; values: string[]; multi: boolean }> = [];
  for (const k of keys) {
    const v = intake[k];
    if (v == null || v === '') continue;
    const arr = Array.isArray(v) ? v : [v];
    const values = arr.map((x) => String(x).trim()).filter((s) => s !== '');
    if (values.length === 0) continue;
    out.push({ key: k, label: labels[k] ?? humanizeKey(k), values, multi: Array.isArray(v) });
  }
  return out;
}

interface BookingSource {
  kind: 'ad' | 'fb' | 'direct';
  /** WHICH booking surface, and — on the quiz page — whether the quiz was
   *  actually taken. 'quiz' = completed it; 'quizpage' = landed there and
   *  booked without finishing. */
  page: 'quiz' | 'quizpage' | 'lp' | 'unknown';
  adName: string;
  campaign: string;
  adset: string;
  adId: string;
}
/** Derive the ad that drove a booking from its captured landing URL. The widget
 *  stores the full pageUrl, whose UTM/ad params (utm_content=ad name, ad_id,
 *  utm_campaign, utm_term=ad set) name the exact ad when the Meta ad URLs are
 *  tagged. fbclid (column or in the URL) flags a Meta-ad click even when UTMs
 *  are missing; neither present ⇒ direct/unknown. */
/** Step the quiz reports when the visitor reached the result screen. */
const QUIZ_RESULT_STEP = 10;

function parseSource(
  pageUrl: string | null,
  fbclid: string | null,
  quizMaxStep: number | null,
): BookingSource {
  let p: URLSearchParams | null = null;
  try {
    if (pageUrl) p = new URL(pageUrl).searchParams;
  } catch {
    /* malformed URL — treat as no params */
  }
  const get = (k: string) => p?.get(k)?.trim() ?? '';
  const adName = get('utm_content');
  const adId = get('ad_id');
  const hasFbclid = !!(fbclid || get('fbclid'));
  const kind: BookingSource['kind'] = adName || adId ? 'ad' : hasFbclid ? 'fb' : 'direct';
  // Booking SURFACE — two ways in now: the Treningsprofilen quiz or the
  // classic LP. Derived from the captured pageUrl's path (works retroactively).
  const path = (pageUrl ?? '').toLowerCase();
  const onQuizPage = path.includes('/treningsprofil') || path.includes('/treningskalkulator');
  // Being ON the quiz page is not the same as having TAKEN the quiz: an ad can
  // land someone there who opens the widget straight away. quizMaxStep is the
  // server's own record of how far that session got — 10 = reached the result.
  const page: BookingSource['page'] = onQuizPage
    ? (quizMaxStep ?? 0) >= QUIZ_RESULT_STEP
      ? 'quiz'
      : 'quizpage'
    : pageUrl
      ? 'lp'
      : 'unknown';
  return { kind, page, adName, campaign: get('utm_campaign'), adset: get('utm_term'), adId };
}

function SrcField({
  label,
  value,
  mono,
  good,
}: {
  label: string;
  value: string;
  mono?: boolean;
  good?: boolean;
}) {
  return (
    <div className="flex flex-col gap-1">
      <dt className="text-label font-medium text-fg-muted">{label}</dt>
      <dd
        className={`${good ? 'font-medium text-good-fg' : 'text-fg'} ${mono ? 'font-mono text-label' : 'text-body'}`}
      >
        {value}
      </dd>
    </div>
  );
}

export function BookingQueuePage() {
  const m = useViewMessages(MESSAGES);
  const query = useBookingQueue();
  const [filter, setFilter] = useState<'all' | BookingStatus | 'archived'>('all');
  const [search, setSearch] = useState('');
  const [range, setRange] = useState<{ start: string; end: string } | null>(null);
  const [reconDays, setReconDays] = useState<number>(30);
  const recon = useReconciliation(reconDays);

  const all = query.data?.bookings ?? [];
  const counts = useMemo(() => {
    // Active counts exclude archived rows; `archived` is its own bucket.
    const c = { all: 0, new: 0, synced: 0, error: 0, cancelled: 0, archived: 0 };
    for (const b of all) {
      if (b.archivedAt) {
        c.archived++;
        continue;
      }
      c.all++;
      c[b.status]++;
    }
    return c;
  }, [all]);
  const coworkActive = all.some((b) => (b.syncedBy ?? '').toLowerCase().includes('cowork'));

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return all.filter((b) => {
      const archived = !!b.archivedAt;
      if (filter === 'archived') {
        if (!archived) return false;
      } else {
        if (archived) return false; // archived rows are hidden from active views
        if (filter !== 'all' && b.status !== filter) return false;
      }
      if (range) {
        const k = osloParts(b.slotStartsAt).key;
        if (k < range.start || k > range.end) return false;
      }
      if (q && !`${b.name} ${b.phone} ${b.email} ${b.ref}`.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [all, filter, range, search]);

  const filters: Array<{ key: 'all' | BookingStatus | 'archived'; label: string; count: number }> =
    [
      { key: 'all', label: m.all, count: counts.all },
      { key: 'new', label: m.new, count: counts.new },
      { key: 'synced', label: m.synced, count: counts.synced },
      { key: 'error', label: m.error, count: counts.error },
      { key: 'archived', label: m.archived, count: counts.archived },
    ];

  return (
    <div className="flex flex-col gap-5">
      {/* header */}
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-title text-fg">{m.title}</h1>
          <p className="mt-1 text-body text-fg-muted">{m.sub}</p>
        </div>
        <div className="flex w-full flex-wrap items-center gap-2.5 sm:w-auto">
          <span className="inline-flex min-w-0 items-center gap-2 rounded-lg border border-border bg-surface px-3 py-2 text-label font-medium text-fg-muted">
            <span
              className={`size-2.5 shrink-0 rounded-full ${coworkActive ? 'bg-good-fg' : 'bg-fg-subtle'}`}
              aria-hidden
            />
            <span className="truncate">{coworkActive ? m.coworkActive : m.coworkIdle}</span>
          </span>
          <button
            type="button"
            onClick={() => query.refetch()}
            className="inline-flex items-center gap-2 rounded-lg bg-brand px-4 py-2 text-label font-medium text-brand-fg hover:bg-brand-hover"
          >
            <RefreshCw className="size-4" aria-hidden /> {m.refresh}
          </button>
        </div>
      </header>

      {/* stat cards */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Stat label={m.statNew} value={counts.new} tone="text-warn-fg" />
        <Stat label={m.statSynced} value={counts.synced} tone="text-good-fg" />
        <Stat label={m.statErr} value={counts.error} tone="text-bad-fg" />
        <Stat label={m.statTotal} value={counts.all} tone="text-brand" />
      </div>

      <ReconciliationCard m={m} data={recon.data} days={reconDays} onDays={setReconDays} />

      {/* toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="-mx-1 w-full overflow-x-auto px-1 sm:mx-0 sm:w-auto sm:overflow-visible sm:px-0">
          {/* biome-ignore lint/a11y/useSemanticElements: role=group is correct ARIA for this segmented filter control; a <fieldset> would impose unwanted default styling */}
          <div
            className="inline-flex gap-1 rounded-xl border border-border bg-surface p-1"
            role="group"
            aria-label={m.colStatus}
          >
            {filters.map((f) => (
              <button
                key={f.key}
                type="button"
                aria-pressed={filter === f.key}
                onClick={() => setFilter(f.key)}
                className={
                  (filter === f.key
                    ? 'bg-brand text-brand-fg'
                    : 'text-fg-muted hover:bg-surface-2') +
                  ' whitespace-nowrap rounded-lg px-3.5 py-2 text-label font-medium sm:py-1.5'
                }
              >
                {f.label} <span className="opacity-70">{f.count}</span>
              </button>
            ))}
          </div>
        </div>
        <div className="flex w-full flex-wrap items-center gap-2.5 sm:w-auto">
          <DatePicker m={m} range={range} onApply={setRange} />
          <div className="inline-flex w-full min-w-0 items-center gap-2 rounded-lg border border-border bg-surface px-3 py-2 sm:w-auto sm:min-w-[240px]">
            <Search className="size-4 text-fg-subtle" aria-hidden />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={m.search}
              className="min-w-0 flex-1 bg-transparent text-label text-fg outline-none placeholder:text-fg-subtle"
            />
          </div>
        </div>
      </div>

      {/* table */}
      {query.isLoading ? (
        <LoadingState />
      ) : query.isError ? (
        <ErrorState onRetry={() => query.refetch()} />
      ) : (
        <div className="overflow-x-auto rounded-xl border border-border bg-surface">
          <table className="w-full min-w-[1080px] border-collapse text-body">
            <thead>
              <tr className="border-b border-border bg-surface-2 text-left text-label font-semibold uppercase tracking-wider text-fg-muted">
                <th className="sticky left-0 z-10 bg-surface-2 px-3 py-3.5 sm:px-5">
                  {m.colStatus}
                </th>
                <th className="px-3 py-3.5 sm:px-5">{m.colName}</th>
                <th className="px-3 py-3.5 sm:px-5">{m.colContact}</th>
                <th className="px-3 py-3.5 sm:px-5">{m.colSlot}</th>
                <th className="px-3 py-3.5 sm:px-5">{m.colNote}</th>
                <th className="px-3 py-3.5 sm:px-5">{m.colBooked}</th>
                <th className="px-3 py-3.5 text-right sm:px-5">{m.colAction}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((b) => (
                <Row key={b.id} b={b} m={m} />
              ))}
            </tbody>
          </table>
          {rows.length === 0 ? (
            <div className="px-4 py-12 text-center text-body text-fg-muted">
              {all.length === 0 ? m.emptyAll : m.empty}
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, tone }: { label: string; value: number; tone: string }) {
  return (
    <div className="flex flex-col gap-1.5 rounded-xl border border-border bg-surface px-4 py-4 sm:px-5">
      <span className="text-label font-medium text-fg-muted">{label}</span>
      <span className={`text-display font-bold leading-none ${tone}`}>{value}</span>
    </div>
  );
}

// Aggregate ad-attribution reconciliation: our confirmed-click bookings vs
// Meta's reported Schedule conversions. The gap is the honest, un-faked count of
// ad-influenced bookings Meta credits that we couldn't tie to a click.
function ReconStat({
  label,
  value,
  tone,
}: {
  label: string;
  value: number | undefined;
  tone: string;
}) {
  return (
    <div className="flex flex-col gap-1 rounded-lg border border-border bg-surface-2 px-4 py-3">
      <span className="text-label font-medium text-fg-muted">{label}</span>
      <span className={`text-title font-bold leading-none ${tone}`}>{value ?? '—'}</span>
    </div>
  );
}

function ReconciliationCard({
  m,
  data,
  days,
  onDays,
}: {
  m: ViewMessages;
  data: ReconciliationData | undefined;
  days: number;
  onDays: (d: number) => void;
}) {
  const b = data?.bookings;
  return (
    <section className="rounded-xl border border-border bg-surface p-4 sm:p-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-section font-semibold text-fg">{m.reconTitle}</h2>
          <p className="mt-0.5 text-label text-fg-muted">{m.reconSub}</p>
        </div>
        <div className="inline-flex rounded-lg border border-border bg-surface-2 p-0.5">
          {[7, 30, 90].map((d) => (
            <button
              key={d}
              type="button"
              onClick={() => onDays(d)}
              className={`rounded-md px-3 py-2 text-label font-medium transition-colors sm:py-1.5 ${
                days === d ? 'bg-surface text-fg shadow-sm' : 'text-fg-muted hover:text-fg'
              }`}
            >
              {d}d
            </button>
          ))}
        </div>
      </div>
      <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <ReconStat label={m.reconTotal} value={b?.total} tone="text-fg" />
        <ReconStat label={m.reconConfirmed} value={b?.confirmedFromAd} tone="text-brand" />
        <ReconStat label={m.reconDirect} value={b?.direct} tone="text-fg-muted" />
        <ReconStat label={m.reconMeta} value={data?.metaScheduleConversions} tone="text-good-fg" />
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-2 rounded-lg bg-surface-2 px-4 py-3">
        <span className="text-label text-fg-muted">{m.reconGap}</span>
        <span className="text-section font-bold text-warn-fg">{data ? `≈ ${data.gap}` : '—'}</span>
      </div>
      <p className="mt-3 text-label leading-snug text-fg-subtle">{m.reconNote}</p>
    </section>
  );
}

function Row({ b, m }: { b: BookingRow; m: ViewMessages }) {
  const mutation = useSetSyncStatus();
  const archive = useArchiveBooking();
  const del = useDeleteBooking();
  const isArchived = !!b.archivedAt;
  const src = parseSource(b.pageUrl, b.fbclid, b.quizMaxStep);
  const [open, setOpen] = useState(false);
  const stLabel = { new: m.stNew, synced: m.stSynced, error: m.stError, cancelled: m.stCancelled }[
    b.status
  ];
  const entries = b.intake ? intakeEntries(b.intake, m.intakeLabels) : [];
  const expandable = entries.length > 0 || src.kind !== 'direct';
  const plager = b.intake && typeof b.intake.plager === 'string' ? (b.intake.plager as string) : '';
  const syncInfo =
    b.status === 'synced'
      ? [b.syncedBy, b.syncedAt ? relCreated(b.syncedAt, m) : null].filter(Boolean).join(' · ')
      : b.status === 'error'
        ? (b.errorReason ?? '')
        : '';

  return (
    <>
      <tr className="border-b border-border/60 align-top hover:bg-surface-2/40">
        <td className="sticky left-0 z-10 bg-surface px-3 py-5 sm:px-5">
          <div className="flex flex-col items-start gap-1.5">
            <span
              className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-label font-semibold ${STATUS_TONE[b.status]}`}
            >
              <span className={`size-1.5 rounded-full ${STATUS_DOT[b.status]}`} aria-hidden />{' '}
              {stLabel}
            </span>
            {syncInfo ? (
              <span
                className={`max-w-[160px] text-label font-medium leading-tight ${b.status === 'error' ? 'text-bad-fg' : 'text-fg-subtle'}`}
              >
                {syncInfo}
              </span>
            ) : null}
          </div>
        </td>
        <td className="whitespace-nowrap px-3 py-5 sm:px-5">
          <div className="flex flex-col items-start gap-0.5">
            <span className="text-section font-semibold text-fg">{b.name}</span>
            <span className="font-mono text-label text-fg-subtle">{b.ref}</span>
            {src.page !== 'unknown' ? (
              <span
                className={`mt-1 inline-flex items-center rounded-full px-2 py-0.5 text-label font-semibold ${
                  src.page === 'quiz' ? 'bg-brand/10 text-brand' : 'bg-surface-2 text-fg-muted'
                }`}
                title={src.page === 'quizpage' ? m.pageQuizPageHint : undefined}
              >
                {src.page === 'quiz'
                  ? m.pageQuiz
                  : src.page === 'quizpage'
                    ? m.pageQuizPage
                    : m.pageLp}
              </span>
            ) : null}
            {b.momenceMemberId ? (
              <span className="mt-1 inline-flex items-center gap-1 rounded-full bg-good-tint px-2 py-0.5 text-label font-medium text-good-fg">
                ✓ {m.custOk} #{b.momenceMemberId}
              </span>
            ) : b.momenceMemberError ? (
              <span
                title={b.momenceMemberError}
                className="mt-1 inline-flex items-center gap-1 rounded-full bg-warn-tint px-2 py-0.5 text-label font-medium text-warn-fg"
              >
                {m.custFail}
              </span>
            ) : null}
          </div>
        </td>
        <td className="px-3 py-5 sm:px-5">
          <div className="flex flex-col gap-0.5">
            <span className="font-mono text-body text-fg">{b.phone}</span>
            <span className="text-body text-fg-muted">{b.email}</span>
          </div>
        </td>
        <td className="px-3 py-5 sm:px-5">
          <div className="flex flex-col gap-0.5">
            <span className="whitespace-nowrap text-section font-semibold text-brand">
              {osloDay(b.slotStartsAt, m)}
            </span>
            <span className="font-mono text-body text-fg-muted">
              {m.atTime(osloTime(b.slotStartsAt))}
            </span>
          </div>
        </td>
        <td className="max-w-[280px] px-3 py-5 sm:px-5">
          <div className="flex flex-col items-start gap-1.5">
            {src.kind === 'ad' ? (
              <span
                title={src.adName}
                className="inline-flex max-w-full items-center rounded-full bg-brand-tint px-2.5 py-1 text-label font-medium text-brand"
              >
                <span className="truncate">📣 {src.adName}</span>
              </span>
            ) : src.kind === 'fb' ? (
              <span className="inline-flex items-center gap-1 rounded-full bg-brand-tint px-2.5 py-1 text-label font-medium text-brand">
                📣 {m.srcFacebook}
              </span>
            ) : null}
            {expandable ? (
              <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                aria-expanded={open}
                className="flex max-w-full flex-col items-start gap-1 text-left"
              >
                {plager ? (
                  <span className="inline-block rounded-lg bg-brand-tint px-2.5 py-1.5 text-label leading-snug text-fg">
                    {plager}
                  </span>
                ) : null}
                <span className="inline-flex items-center gap-1 text-label font-medium text-brand">
                  {open
                    ? m.hideAnswers
                    : entries.length > 0
                      ? `${m.viewAnswers} (${entries.length})`
                      : m.srcViewDetails}
                  <ChevronDown
                    className={`size-3.5 transition-transform ${open ? 'rotate-180' : ''}`}
                    aria-hidden
                  />
                </span>
              </button>
            ) : (
              <span className="text-label text-fg-subtle">—</span>
            )}
          </div>
        </td>
        <td className="whitespace-nowrap px-3 py-5 text-body text-fg-muted sm:px-5">
          {relCreated(b.createdAt, m)}
        </td>
        <td className="whitespace-nowrap px-3 py-5 text-right sm:px-5">
          <div className="flex flex-col items-end gap-2.5">
            {!isArchived ? (
              b.status === 'new' ? (
                <button
                  type="button"
                  disabled={mutation.isPending}
                  onClick={() => mutation.mutate({ id: b.id, status: 'synced' })}
                  className="rounded-md bg-brand px-4 py-2 text-body font-semibold text-brand-fg transition-colors hover:bg-brand-hover disabled:opacity-50"
                >
                  {m.markSynced}
                </button>
              ) : b.status === 'synced' ? (
                <span className="inline-flex items-center gap-1.5 rounded-full bg-good-tint px-3 py-1.5 text-label font-semibold text-good-fg">
                  ✓ {m.inMomence}
                </span>
              ) : b.status === 'error' ? (
                <button
                  type="button"
                  disabled={mutation.isPending}
                  onClick={() => mutation.mutate({ id: b.id, status: 'new' })}
                  className="rounded-md border border-bad-fg/40 bg-bad-tint px-4 py-2 text-body font-semibold text-bad-fg transition-colors disabled:opacity-50"
                >
                  {m.retry}
                </button>
              ) : null
            ) : null}
            <div className="flex items-center gap-1.5 sm:gap-1">
              {!isArchived && (b.status === 'synced' || b.status === 'cancelled') ? (
                <button
                  type="button"
                  disabled={mutation.isPending}
                  onClick={() => mutation.mutate({ id: b.id, status: 'new' })}
                  className="rounded-md px-3 py-2 text-label font-medium text-fg-muted transition-colors hover:bg-surface-2 hover:text-fg disabled:opacity-50 sm:px-2.5 sm:py-1.5"
                >
                  {m.undo}
                </button>
              ) : null}
              <button
                type="button"
                disabled={archive.isPending}
                onClick={() => archive.mutate({ id: b.id, archived: !isArchived })}
                className="rounded-md px-3 py-2 text-label font-medium text-fg-muted transition-colors hover:bg-surface-2 hover:text-fg disabled:opacity-50 sm:px-2.5 sm:py-1.5"
              >
                {isArchived ? m.restore : m.archive}
              </button>
              <button
                type="button"
                disabled={del.isPending}
                onClick={() => {
                  if (window.confirm(m.confirmDelete)) del.mutate({ id: b.id });
                }}
                className="rounded-md px-3 py-2 text-label font-medium text-bad-fg/80 transition-colors hover:bg-bad-tint hover:text-bad-fg disabled:opacity-50 sm:px-2.5 sm:py-1.5"
              >
                {m.del}
              </button>
            </div>
          </div>
        </td>
      </tr>
      {open && expandable ? (
        <tr className="border-b border-border/60 bg-surface-2/30">
          <td colSpan={7} className="px-4 pb-4 pt-0">
            <div className="flex flex-col gap-4 rounded-xl border border-border bg-surface p-4">
              {src.kind !== 'direct' ? (
                <div>
                  <span className="mb-3 block text-label font-semibold uppercase tracking-wider text-fg-muted">
                    {m.srcTitle}
                  </span>
                  <dl className="grid gap-x-6 gap-y-3 sm:grid-cols-2 sm:gap-x-8">
                    {src.adName ? <SrcField label={m.srcAd} value={src.adName} /> : null}
                    {!src.adName && !src.adId ? (
                      <SrcField label={m.srcAd} value={m.srcFacebook} />
                    ) : null}
                    {src.campaign ? <SrcField label={m.srcCampaign} value={src.campaign} /> : null}
                    {src.adset ? <SrcField label={m.srcAdset} value={src.adset} /> : null}
                    {src.adId ? <SrcField label={m.srcAdId} value={src.adId} mono /> : null}
                    <SrcField
                      label={m.srcMeta}
                      value={b.capiStatus === 'sent' ? m.capiSent : (b.capiStatus ?? '—')}
                      good={b.capiStatus === 'sent'}
                    />
                  </dl>
                </div>
              ) : null}
              {entries.length > 0 ? (
                <div>
                  <span className="mb-3 block text-label font-semibold uppercase tracking-wider text-fg-muted">
                    {m.intakeTitle}
                  </span>
                  <dl className="grid gap-x-6 gap-y-3 sm:grid-cols-2 sm:gap-x-8">
                    {entries.map((e) => (
                      <div key={e.key} className="flex flex-col gap-1">
                        <dt className="text-label font-medium text-fg-muted">{e.label}</dt>
                        <dd className="flex flex-wrap gap-1.5">
                          {e.multi ? (
                            e.values.map((val) => (
                              <span
                                key={val}
                                className="inline-block rounded-md bg-brand-tint px-2 py-0.5 text-label text-fg"
                              >
                                {val}
                              </span>
                            ))
                          ) : (
                            <span className="text-body text-fg">{e.values.join(', ')}</span>
                          )}
                        </dd>
                      </div>
                    ))}
                  </dl>
                </div>
              ) : null}
            </div>
          </td>
        </tr>
      ) : null}
    </>
  );
}

function DatePicker({
  m,
  range,
  onApply,
}: {
  m: ViewMessages;
  range: { start: string; end: string } | null;
  onApply: (r: { start: string; end: string } | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const T = todayKey();
  const [calY, setCalY] = useState(() => Number(T.split('-')[0]));
  const [calM, setCalM] = useState(() => Number(T.split('-')[1]) - 1);
  const [pStart, setPStart] = useState(range?.start ?? '');
  const [pEnd, setPEnd] = useState(range?.end ?? '');

  const presets: Array<{ key: string; label: string; s: string; e: string }> = [
    { key: 'all', label: m.allDates, s: '', e: '' },
    { key: 'today', label: m.today, s: T, e: T },
    { key: 'tomorrow', label: m.tomorrow, s: addDaysKey(T, 1), e: addDaysKey(T, 1) },
    { key: 'next7', label: m.next7, s: T, e: addDaysKey(T, 6) },
    { key: 'next30', label: m.next30, s: T, e: addDaysKey(T, 29) },
  ];
  const label = range
    ? range.start === range.end
      ? fmtShort(range.start, m)
      : `${fmtShort(range.start, m)} – ${fmtShort(range.end, m)}`
    : m.allDates;

  const pickDay = (key: string) => {
    if (!pStart || (pStart && pEnd)) {
      setPStart(key);
      setPEnd('');
    } else if (key < pStart) setPStart(key);
    else setPEnd(key);
  };
  const apply = () => {
    onApply(pStart ? { start: pStart, end: pEnd || pStart } : null);
    setOpen(false);
  };

  const lead = (new Date(calY, calM, 1).getDay() + 6) % 7;
  const dim = new Date(calY, calM + 1, 0).getDate();
  const cells: Array<{ key: string; d: number } | null> = [];
  for (let i = 0; i < lead; i++) cells.push(null);
  for (let d = 1; d <= dim; d++) cells.push({ key: mk(calY, calM, d), d });
  const rangeText = pStart
    ? `${fmtShort(pStart, m)} – ${pEnd ? fmtShort(pEnd, m) : '…'}`
    : m.pickDates;

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`inline-flex items-center gap-2 rounded-lg border bg-surface px-3 py-2 text-label font-medium text-fg ${range ? 'border-brand' : 'border-border'}`}
      >
        <Calendar className="size-4 text-fg-subtle" aria-hidden /> {label}
        <span className="text-fg-subtle">▾</span>
      </button>
      {open ? (
        <>
          <button
            type="button"
            aria-label={m.close}
            className="fixed inset-0 z-30 cursor-default"
            onClick={() => setOpen(false)}
          />
          <div className="absolute right-0 top-[calc(100%+8px)] z-40 flex w-[calc(100vw-2rem)] max-w-[540px] flex-col gap-4 rounded-xl border border-border bg-surface p-4 shadow-2xl sm:w-[540px] sm:flex-row">
            <div className="flex w-full flex-col gap-0.5 border-b border-border pb-3 sm:w-40 sm:border-b-0 sm:border-r sm:pb-0 sm:pr-3">
              <span className="px-2 pb-1.5 pt-0.5 text-label font-semibold uppercase tracking-wider text-fg-muted">
                {m.shortcuts}
              </span>
              {presets.map((p) => (
                <button
                  key={p.key}
                  type="button"
                  onClick={() => {
                    setPStart(p.s);
                    setPEnd(p.e);
                  }}
                  className="rounded-md px-3 py-2 text-left text-label font-medium text-fg-muted hover:bg-surface-2"
                >
                  {p.label}
                </button>
              ))}
            </div>
            <div className="flex flex-1 flex-col gap-2.5">
              <div className="flex items-center justify-between">
                <button
                  type="button"
                  aria-label={m.prevMonth}
                  onClick={() => {
                    const mm = calM - 1;
                    if (mm < 0) {
                      setCalM(11);
                      setCalY(calY - 1);
                    } else setCalM(mm);
                  }}
                  className="grid size-9 place-items-center rounded-md border border-border text-fg hover:bg-surface-2 sm:size-8"
                >
                  <ChevronLeft className="size-4" />
                </button>
                <span className="text-label font-semibold capitalize text-fg">
                  {m.monL[calM]} {calY}
                </span>
                <button
                  type="button"
                  aria-label={m.nextMonth}
                  onClick={() => {
                    const mm = calM + 1;
                    if (mm > 11) {
                      setCalM(0);
                      setCalY(calY + 1);
                    } else setCalM(mm);
                  }}
                  className="grid size-9 place-items-center rounded-md border border-border text-fg hover:bg-surface-2 sm:size-8"
                >
                  <ChevronRight className="size-4" />
                </button>
              </div>
              <div className="grid grid-cols-7 gap-0.5">
                {m.wd.map((w) => (
                  <span
                    key={w}
                    className="py-1 text-center text-label font-semibold text-fg-subtle"
                  >
                    {w}
                  </span>
                ))}
                {cells.map((c, i) => {
                  // biome-ignore lint/suspicious/noArrayIndexKey: leading empty calendar cells have no stable id; their index is positionally fixed
                  if (!c) return <span key={`x${i}`} />;
                  const isStart = c.key === pStart,
                    isEnd = !!pEnd && c.key === pEnd;
                  const inRange = !!pStart && !!pEnd && c.key > pStart && c.key < pEnd;
                  const endpoint = isStart || isEnd;
                  return (
                    <button
                      key={c.key}
                      type="button"
                      onClick={() => pickDay(c.key)}
                      className={
                        'h-10 rounded-md text-label sm:h-9 ' +
                        (endpoint
                          ? 'bg-brand font-semibold text-brand-fg'
                          : inRange
                            ? 'bg-brand-tint text-brand'
                            : c.key === T
                              ? 'font-semibold text-brand hover:bg-surface-2'
                              : 'text-fg hover:bg-surface-2')
                      }
                    >
                      {c.d}
                    </button>
                  );
                })}
              </div>
              <div className="mt-1 flex items-center justify-between gap-2 border-t border-border pt-3">
                <span className="text-label text-fg-muted">{rangeText}</span>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setOpen(false)}
                    className="rounded-md border border-border px-4 py-1.5 text-label font-medium text-fg hover:bg-surface-2"
                  >
                    {m.cancel}
                  </button>
                  <button
                    type="button"
                    onClick={apply}
                    className="rounded-md bg-brand px-4 py-1.5 text-label font-semibold text-brand-fg hover:bg-brand-hover"
                  >
                    {m.apply}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}
